import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { RefreshCw, Bell, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only page that surfaces exactly what the service worker observed
 * on the LAST 20 push events — payload received, badge_count value,
 * whether navigator.setAppBadge resolved or rejected, and why. Purpose:
 * stop guessing at Android TWA badge behaviour; we now see the ground
 * truth from the device.
 */
export default function PushDiagnostics() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const dbi = await new Promise((resolve, reject) => {
        const req = indexedDB.open('expatssphere-push-diag', 1);
        req.onupgradeneeded = () => {
          const d = req.result;
          if (!d.objectStoreNames.contains('events')) {
            d.createObjectStore('events', { keyPath: 'ts' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      const rows = await new Promise((resolve) => {
        const tx = dbi.transaction('events', 'readonly');
        const req = tx.objectStore('events').getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => resolve([]);
      });
      dbi.close();
      rows.sort((a, b) => b.ts - a.ts);
      setEvents(rows);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const sendTestPush = async () => {
    setSending(true);
    try {
      // Send the *current* device's endpoint as a hint so the server tests
      // this device rather than whichever subscription row it reads first.
      let endpointHint = '';
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) endpointHint = sub.toJSON().endpoint || '';
      } catch (hintErr) {
        // Non-fatal: fall back to an unhinted test.
      }
      const testUrl = endpointHint
        ? `${API}/push/test?endpoint_hint=${encodeURIComponent(endpointHint.substring(0, 60))}`
        : `${API}/push/test`;
      await axios.post(testUrl, { badge_count: 3 });
      toast.success('Test push sent — wait a few seconds then Refresh');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test push failed');
    } finally {
      setSending(false);
    }
  };

  const clearLog = async () => {
    try {
      const dbi = await new Promise((resolve) => {
        const req = indexedDB.open('expatssphere-push-diag', 1);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      });
      if (!dbi) return;
      const tx = dbi.transaction('events', 'readwrite');
      tx.objectStore('events').clear();
      tx.oncomplete = () => { dbi.close(); setEvents([]); toast.success('Log cleared'); };
    } catch (_) { /* ignore */ }
  };

  if (!user) return <div className="p-8">Please sign in.</div>;
  if (!user.is_admin) return <div className="p-8">Admin only.</div>;

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 pt-20">
      <h1 className="text-2xl md:text-3xl font-bold mb-2">Push Diagnostics</h1>
      <p className="text-sm text-muted-foreground mb-4">
        The last 20 push events observed by the service worker on THIS device.
        Send a test push, wait a few seconds, then tap Refresh.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={sendTestPush} disabled={sending} className="gap-2" data-testid="send-test-push">
          <Bell className="w-4 h-4" />
          {sending ? 'Sending…' : 'Send test push'}
        </Button>
        <Button onClick={loadEvents} variant="outline" className="gap-2" data-testid="refresh-diag">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
        <Button onClick={clearLog} variant="ghost" className="gap-2 text-muted-foreground">
          <Trash2 className="w-4 h-4" />
          Clear log
        </Button>
      </div>

      {loading && <p>Loading…</p>}
      {!loading && events.length === 0 && (
        <div className="rounded-2xl border border-dashed p-8 text-center text-muted-foreground">
          <p className="font-semibold">No push events logged yet.</p>
          <p className="text-sm mt-1">Send a test push above, wait ~5s for it to arrive, then Refresh.</p>
        </div>
      )}

      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.ts} className="border rounded-xl p-4 bg-white text-sm space-y-1" data-testid={`diag-entry-${e.ts}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-muted-foreground">{new Date(e.ts).toLocaleString()}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                e.setAppBadgeOutcome === 'resolved' ? 'bg-emerald-100 text-emerald-700'
                : e.setAppBadgeOutcome === 'rejected' ? 'bg-rose-100 text-rose-700'
                : 'bg-gray-100 text-gray-700'
              }`}>
                setAppBadge: {e.setAppBadgeOutcome}
              </span>
            </div>
            <div><b>hasSetAppBadge:</b> {String(e.hasSetAppBadge)}</div>
            <div><b>rawBadgeCount:</b> {String(e.rawBadgeCount)} <span className="text-muted-foreground">({e.rawBadgeType})</span></div>
            <div><b>computedBadgeCount:</b> {e.computedBadgeCount}</div>
            <div><b>existingNotifications:</b> {e.existingNotifications}</div>
            {e.setAppBadgeError && <div className="text-rose-700"><b>error:</b> {e.setAppBadgeError}</div>}
            <details className="text-xs text-muted-foreground mt-2">
              <summary className="cursor-pointer">Raw payload</summary>
              <pre className="whitespace-pre-wrap break-all bg-gray-50 p-2 mt-1 rounded">
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            </details>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">User agent</summary>
              <div className="break-all">{e.userAgent}</div>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
}
